import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import UserPreferences from '../UserPreferences';
import LoadingSpinner from '../common/LoadingSpinner';
import './PostpartumSection.css';

function PostpartumNutrition() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [postpartumInfo, setPostpartumInfo] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [infoData, profileData] = await Promise.all([
        api.getPostpartumInfo(),
        api.getPregnancyProfile(userId).catch(() => null)
      ]);
      setPostpartumInfo(infoData);
      setProfile(profileData);
    } catch (error) {
      console.error('Error loading postpartum nutrition:', error);
      setError(t('postpartum.error.load', 'Unable to load postpartum data. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    loadData();
  }, [userId, loadData]);

  if (loading) {
    return (
      <div className="postpartum-section">
        <LoadingSpinner message={t('postpartum.loading.nutrition', 'Loading nutrition information...')} />
      </div>
    );
  }

  return (
    <div className="postpartum-section">
      <div className="section-header">
        <button className="btn-back" onClick={() => navigate(`/postpartum/${userId}`)}>
          ← {t('action.back', 'Back')}
        </button>
        <h1>🥗 {t('postpartum.title.nutrition', 'Postpartum Nutrition')}</h1>
        <nav className="postpartum-nav">
          <Link to={`/postpartum/${userId}/overview`} className="nav-link">{t('postpartum.nav.overview', 'Overview')}</Link>
          <Link to={`/postpartum/${userId}/recovery`} className="nav-link">{t('postpartum.nav.recovery', 'Recovery')}</Link>
          <Link to={`/postpartum/${userId}/nutrition`} className="nav-link active">{t('postpartum.nav.nutrition', 'Nutrition')}</Link>
          <Link to={`/postpartum/${userId}/appointments`} className="nav-link">{t('postpartum.nav.appointments', 'Appointments')}</Link>
          <Link to={`/postpartum/${userId}/warnings`} className="nav-link">{t('postpartum.nav.warnings', 'Warnings')}</Link>
        </nav>
      </div>

      <div className="section-content">
        {error && <div className="postpartum-error">{error}</div>}
        {postpartumInfo?.nutrition && (
          <>
            {postpartumInfo.nutrition.immediate && (
              <section className="data-section immediate-nutrition-section">
                <h2>⚡ {t('postpartum.immediate.nutrition', 'Immediate Postpartum Nutrition')}</h2>
                <div className="nutrient-foods-grid">
                  <div className="nutrient-category">
                    <h3>🍽️ {t('postpartum.nutrition.focus', 'Focus Areas')}</h3>
                    <ul>
                      {postpartumInfo.nutrition.immediate.focus?.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            )}

            {profile?.isBreastfeeding && postpartumInfo.nutrition.breastfeeding && (
              <section className="data-section breastfeeding-nutrition-section">
                <h2>🤱 {t('postpartum.breastfeeding.nutrition', 'Nutrition While Breastfeeding')}</h2>
                <div className="nutrient-foods-grid">
                  <div className="nutrient-category">
                    <h3>🔥 {t('postpartum.calories', 'Calories')}</h3>
                    <p>{postpartumInfo.nutrition.breastfeeding.calories}</p>
                  </div>
                  <div className="nutrient-category">
                    <h3>🥗 {t('postpartum.key.nutrients', 'Key Nutrients')}</h3>
                    <ul>
                      {postpartumInfo.nutrition.breastfeeding.nutrients?.map((nutrient, i) => (
                        <li key={i}>{nutrient}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="nutrient-category">
                    <h3>🍽️ {t('postpartum.recommended.foods', 'Recommended Foods')}</h3>
                    <ul>
                      {postpartumInfo.nutrition.breastfeeding.foods?.map((food, i) => (
                        <li key={i}>{food}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            )}

            {profile?.isBreastfeeding === false && postpartumInfo.nutrition.notBreastfeeding && (
              <section className="data-section not-breastfeeding-nutrition-section">
                <h2>🍽️ {t('postpartum.not.breastfeeding.nutrition', 'Nutrition (Not Breastfeeding)')}</h2>
                <div className="nutrient-foods-grid">
                  <div className="nutrient-category">
                    <h3>🥗 {t('postpartum.nutrition.focus', 'Focus Areas')}</h3>
                    <ul>
                      {postpartumInfo.nutrition.notBreastfeeding.focus?.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            )}

            {postpartumInfo.nutrition.hydration && (
              <section className="data-section hydration-section">
                <h2>💧 {t('postpartum.hydration', 'Hydration')}</h2>
                <div className="nutrient-foods-grid">
                  <div className="nutrient-category">
                    <h3>💧 {t('postpartum.hydration.info', 'Hydration Information')}</h3>
                    <p style={{marginBottom: '10px'}}>
                      <strong>{postpartumInfo.nutrition.hydration.importance}</strong>
                    </p>
                    <p style={{marginBottom: '10px'}}>
                      <strong>Recommendation:</strong> {postpartumInfo.nutrition.hydration.recommendation}
                    </p>
                    <div>
                      <strong style={{display: 'block', marginBottom: '8px'}}>Tips:</strong>
                      <ul>
                        {postpartumInfo.nutrition.hydration.signs?.map((sign, i) => (
                          <li key={i}>{sign}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="data-section preferences-section">
              <h2>🍽️ {t('preferences.title', 'Food Preferences & Allergies')}</h2>
              <UserPreferences userId={userId} onSave={() => {}} />
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default PostpartumNutrition;

